import React, { useState, useEffect, useMemo } from 'react';
import { Box, Alert, Divider, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShareIcon from '@mui/icons-material/Share';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { useParams } from 'react-router-dom';

import { useGetLessonsByUnitIdQuery } from '../lessons/lessonsApi';
import { useAppSelector, useAppDispatch } from '../../shared/hooks/redux';
import { useObjectPermissionState } from '../../shared/hooks/useCanEdit';
import { useGetCourseByIdQuery } from '../my-courses/coursesApi';
import ShareDialog from '../../shared/components/ShareDialog';

import { useGetUnitByIdQuery, useUpdateUnitMutation } from './unitsApi';
import { UnitOverviewCard } from './components/UnitOverviewCard';

import type { AgentContext } from '@/shared/types/chat';
import type { StoredStandard } from '@/shared/types/customStandard';
import type {
  CreateResourceRequest,
  GenerateFromTemplateRequest,
  Resource,
  ResourceParentLocator,
} from '@/shared/types/resource';
import {
  stripStorageImagePathsFromDocumentContent,
  stripStorageImagePathsFromSlideContent,
} from '@/shared/types/resource';
import { PageSpinner } from '@/shared/components/PageSpinner';
import AccessDeniedOrNotFound from '@/shared/components/AccessDeniedOrNotFound';
import { EditableText } from '@/shared/components/EditableText';
import { setIsMobile, setScopedAiContext, toggleScopedAiPanel } from '@/shared/store/uiSlice';
import ResourceListCard from '@/shared/components/ResourceListCard';
import StandardsCoverageMatrix from '@/shared/components/StandardsCoverageMatrix';
import { exportCurriculumPdf } from '@/shared/services/pdfExport';
import { useSetPublicCascadeMutation } from '@/features/permissions/permissionsApi';
import {
  useCreateFromTemplateMutation,
  useCreateResourceMutation,
  useDeleteResourceMutation,
  useGetResourcesQuery,
  useGetResourceTemplatesQuery,
  useUpdateResourceMutation,
  useGenerateFromTemplateMutation,
} from '@/features/resources/resourcesApi';
import { useGoogleDocsExport } from '@/features/resources/hooks/useGoogleDocsExport';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { ScoperAiDialog } from '@/features/chatbot/scoper-dev';

const UnitPage: React.FC = () => {
  const pdfExportEnabled = isFeatureEnabled('curriculumPdfExport');
  const scoperEnabled = import.meta.env.DEV && isFeatureEnabled('scoperAiDevDialog');
  const [scoperOpen, setScoperOpen] = useState(false);
  const { unitId } = useParams<{ unitId: string }>();
  const { data: unit, error, isLoading } = useGetUnitByIdQuery(unitId!);
  const { data: lessons = [] } = useGetLessonsByUnitIdQuery(unitId!, {
    skip: !unitId,
  });
  // Call course query unconditionally (guarded by skip) to preserve hook order across renders
  const courseIdForScope = unit?.course_id;
  const scoperContext = useMemo<AgentContext | null>(
    () =>
      courseIdForScope && unit
        ? {
            assistantType: 'course',
            courseId: courseIdForScope,
            focus: {
              level: 'unit',
              courseId: courseIdForScope,
              unitId: unit.id,
            },
          }
        : null,
    [courseIdForScope, unit]
  );
  const { data: courseForScope } = useGetCourseByIdQuery(courseIdForScope as string, {
    skip: !courseIdForScope,
  });

  const { user } = useAppSelector((state) => state.auth);
  const isOwner = Boolean(unit && user && unit.owner_id === user.id);
  const unitPermission = useObjectPermissionState('unit', unitId, 'editor', {
    fallbackAllowed: isOwner,
  });
  const canEdit = unitPermission.isAllowed;
  const dispatch = useAppDispatch();
  const [updateUnit, { isLoading: isUpdatingUnit }] = useUpdateUnitMutation();
  const [setPublicCascade] = useSetPublicCascadeMutation();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const unitListLocator: ResourceParentLocator = {
    parentUnitId: unitId!,
    courseId: courseIdForScope ?? undefined,
  };
  const unitWriteLocator: ResourceParentLocator = {
    parentUnitId: unitId!,
    courseId: courseIdForScope ?? undefined,
  };
  const canManageResources = canEdit;
  const { data: resources = [] } = useGetResourcesQuery(unitListLocator, { skip: !unitId });
  const { data: templates = [] } = useGetResourceTemplatesQuery({ templateKind: 'document' });
  const [createResource] = useCreateResourceMutation();
  const [updateResource] = useUpdateResourceMutation();
  const [deleteResource] = useDeleteResourceMutation();
  const [createFromTemplate] = useCreateFromTemplateMutation();
  const [generateFromTemplate] = useGenerateFromTemplateMutation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { exportToGoogle } = useGoogleDocsExport({ onError: setExportError });

  useEffect(() => {
    dispatch(setIsMobile(isMobile));
  }, [isMobile, dispatch]);

  useEffect(() => {
    if (!unit) return;
    dispatch(
      setScopedAiContext({
        level: 'unit',
        courseId: unit.course_id,
        unitId: unit.id,
        resourceId: undefined,
        resourceKind: undefined,
      })
    );
  }, [dispatch, unit]);

  const handleUpdateTitle = async (newTitle: string) => {
    if (!unit) return;

    await updateUnit({
      id: unit.id,
      title: newTitle,
    }).unwrap();
  };

  const handleUpdateDescription = async (newDescription: string) => {
    if (!unit) return;

    await updateUnit({
      id: unit.id,
      description: newDescription,
    }).unwrap();
  };

  const handleExportPdf = async () => {
    if (!unit) return;
    setExportError(null);
    setIsExportingPdf(true);
    try {
      await exportCurriculumPdf('unit', unit.id, `${unit.title || 'unit'}-unit-export.pdf`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export PDF';
      setExportError(message);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleCreateResource = async (resourceData: CreateResourceRequest) => {
    if (!unit) return;
    return createResource({
      ...unitWriteLocator,
      resource: resourceData,
    }).unwrap();
  };

  const handleUpdateResource = async (resource: Resource) => {
    await updateResource({
      id: resource.id,
      title: resource.title,
      description: resource.description,
      content: resource.content,
      version: (resource.version ?? 1) + 1,
    }).unwrap();
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!unit) return;
    await deleteResource({
      ...unitWriteLocator,
      resourceId,
    }).unwrap();
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    if (!unit) return;
    return createFromTemplate({
      ...unitWriteLocator,
      templateId,
    }).unwrap();
  };

  const handleGenerateFromTemplate = async (request: GenerateFromTemplateRequest) => {
    return generateFromTemplate(request).unwrap();
  };

  const handleDuplicateResource = async (resourceData: Resource) => {
    if (!unit || resourceData.resourceKind === 'file') return;
    const clonedContent = JSON.parse(JSON.stringify(resourceData.content ?? {}));
    const duplicateContent =
      resourceData.resourceKind === 'document'
        ? stripStorageImagePathsFromDocumentContent(clonedContent)
        : resourceData.resourceKind === 'slide'
          ? stripStorageImagePathsFromSlideContent(clonedContent)
          : clonedContent;

    return createResource({
      ...unitWriteLocator,
      resource: {
        resourceKind: resourceData.resourceKind,
        title: `Copy of ${resourceData.title}`,
        description: resourceData.description,
        content: duplicateContent,
      },
    }).unwrap();
  };

  // Collect course-level standards from overview items to use as the unit's parentScope
  const courseParentScope = useMemo(() => {
    const map = new Map<string, StoredStandard>();
    (courseForScope?.content?.overview ?? [])
      .filter((it: any) => it?.type === 'standards')
      .flatMap((it: any) => it?.standards ?? [])
      .forEach((s: StoredStandard) => map.set(s.id, s));
    return Array.from(map.values());
  }, [courseForScope]);

  // Prepare coverage matrix for lessons within this unit
  const lessonColumns = useMemo(
    () => (lessons ?? []).map((l, idx) => ({ id: l.id, label: l.title || `Lesson ${idx + 1}` })),
    [lessons]
  );

  const getLessonStandards = (l: any): StoredStandard[] =>
    (l?.content?.overview ?? [])
      .filter((it: any) => it?.type === 'standards')
      .flatMap((it: any) => it?.standards ?? []) as StoredStandard[];

  const lessonStandardRows = useMemo(() => {
    const map = new Map<string, StoredStandard>();
    (lessons ?? []).forEach((l) => getLessonStandards(l).forEach((s) => map.set(s.id, s)));
    return Array.from(map.values());
  }, [lessons]);

  const coverageByLesson = useMemo(() => {
    const map: Record<string, StoredStandard[]> = {};
    (lessons ?? []).forEach((l) => {
      map[l.id] = getLessonStandards(l);
    });
    return map;
  }, [lessons]);

  // Loading / Error states placed AFTER all hooks
  if (isLoading) {
    return <PageSpinner />;
  }
  if (error || !unit) {
    return <AccessDeniedOrNotFound objectType="unit" objectId={unitId} user={user} />;
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          columnGap: 2,
          rowGap: 1,
          alignItems: 'start',
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <EditableText
            value={unit?.title ?? ''}
            onSave={handleUpdateTitle}
            canEdit={canEdit}
            isLoading={isUpdatingUnit}
            typographyProps={{
              variant: 'h2',
              component: 'h1',
            }}
            textFieldProps={{
              sx: {
                '& .MuiInputBase-input': {
                  fontSize: '2.125rem',
                  fontWeight: 300,
                  lineHeight: 1.235,
                },
              },
            }}
          />
        </Box>
        {((!isMobile && user) || canEdit) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {!isMobile && user && pdfExportEnabled && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PictureAsPdfOutlinedIcon />}
                onClick={handleExportPdf}
                disabled={isExportingPdf}
              >
                {isExportingPdf ? 'Exporting...' : 'Export PDF'}
              </Button>
            )}
            {canEdit &&
              (isMobile ? (
                <>
                  <IconButton size="small" onClick={() => setShareOpen(true)} title="Share">
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => dispatch(toggleScopedAiPanel())}
                    title="AI CoPlanner"
                  >
                    <AutoAwesomeIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareOpen(true)}
                  >
                    Share
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => dispatch(toggleScopedAiPanel())}
                  >
                    AI CoPlanner
                  </Button>
                  {scoperEnabled && scoperContext && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setScoperOpen(true)}
                      sx={{ borderStyle: 'dashed' }}
                    >
                      Dev AI
                    </Button>
                  )}
                </>
              ))}
          </Box>
        )}
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <EditableText
            value={unit?.description ?? ''}
            onSave={handleUpdateDescription}
            canEdit={canEdit}
            isLoading={isUpdatingUnit}
            allowEmpty
            multiline
            typographyProps={{
              variant: 'subtitle1',
              color: 'text.secondary',
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Overview */}
      {unit && (
        <Box sx={{ mb: 4 }}>
          <UnitOverviewCard unit={unit} canEdit={canEdit} parentScope={courseParentScope} />
        </Box>
      )}

      {/* Resources */}
      {unit && (
        <Box sx={{ mb: 4 }}>
          <ResourceListCard
            locator={unitListLocator}
            disableGenerate={!courseIdForScope}
            resources={resources}
            templates={templates}
            onCreateResource={handleCreateResource}
            onCreateFromTemplate={handleCreateFromTemplate}
            onGenerateFromTemplate={handleGenerateFromTemplate}
            onUpdateResource={handleUpdateResource}
            onDelete={handleDeleteResource}
            onDuplicateResource={handleDuplicateResource}
            onExportToGoogle={exportToGoogle}
            canEdit={canManageResources}
            title="Unit Resources"
          />
        </Box>
      )}

      {/* Standards Coverage */}
      {unit && (
        <Box sx={{ mb: 4 }}>
          <StandardsCoverageMatrix
            rows={lessonStandardRows}
            columns={lessonColumns}
            coverageByColumn={coverageByLesson}
            title="Standards Coverage Across Lessons"
            subtitle="Which lessons include each standard"
            dense
          />
        </Box>
      )}
      {unit && (
        <ShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          objectId={unit.id}
          objectType="unit"
          ownerId={unit.owner_id}
          isPublic={unit.is_public}
          onTogglePublic={(val) =>
            setPublicCascade({ object_type: 'unit', object_id: unit.id, is_public: val }).unwrap()
          }
          shareUrl={
            courseIdForScope
              ? `${window.location.origin}/curriculum/courses/${courseIdForScope}/units/${unit.id}`
              : undefined
          }
        />
      )}
      {scoperEnabled && scoperContext && (
        <ScoperAiDialog
          open={scoperOpen}
          onClose={() => setScoperOpen(false)}
          context={scoperContext}
        />
      )}
    </Box>
  );
};

export default UnitPage;
